import Image from 'next/image'
import { cn } from '@/utilities/ui'

export interface ThemeIconProps {
  /**
   * Size of the icon. Can be a Tailwind size class (e.g., 'size-4', 'size-6', 'size-8')
   * or a numeric value in pixels (e.g., 16, 24, 32).
   * Defaults to 'size-6' (24px)
   */
  size?: string | number
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Alt text for accessibility. Defaults to empty string with aria-hidden
   */
  alt?: string
  /**
   * Whether the icon should be hidden from screen readers
   * @default true
   */
  ariaHidden?: boolean
  /**
   * Priority loading for Next.js Image
   * @default false
   */
  priority?: boolean
}

/**
 * Theme-aware icon component that displays the appropriate icon based on theme (light/dark)
 * Uses Icon-pos.svg for light theme and Icon-neg.svg for dark theme
 */
export default function ThemeIcon({
  size = 'size-6',
  className = '',
  alt = '',
  ariaHidden = true,
  priority = false,
}: ThemeIconProps) {
  // Convert numeric size to Tailwind class if needed
  const sizeClass = typeof size === 'number' ? `size-${size}` : size

  // Calculate sizes attribute for Next.js Image based on size prop
  const getSizes = () => {
    if (typeof size === 'number') {
      return `${size}px`
    }
    // Handle responsive classes like "size-7 sm:size-8" - extract the largest size
    const matches = size.match(/size-(\d+)/g)
    if (matches && matches.length > 0) {
      // Get the largest size from all matches (for responsive classes)
      const numbers = matches.map((m) => parseInt(m.replace('size-', ''), 10))
      const maxNum = Math.max(...numbers)
      return `${maxNum * 4}px`
    }
    // Fallback for single size class
    const match = size.match(/size-(\d+)/)
    if (match) {
      const num = parseInt(match[1], 10)
      return `${num * 4}px`
    }
    return '24px' // default
  }

  return (
    <span className={cn('relative block', sizeClass, className)}>
      {/* Light theme icon */}
      <Image
        src="/images/logo/Icon-pos.svg"
        alt={alt}
        fill
        sizes={getSizes()}
        priority={priority}
        className="only-light"
        aria-hidden={ariaHidden}
      />
      {/* Dark theme icon */}
      <Image
        src="/images/logo/Icon-neg.svg"
        alt={alt}
        fill
        sizes={getSizes()}
        priority={priority}
        className="only-dark"
        aria-hidden={ariaHidden}
      />
    </span>
  )
}
